/**
 * Normalizes the array path to be both JSONata and lodash compatible.
 * @example removeDotsFromArrayPath("foo.[1].bar.[0].baz") => "foo[1].bar[0].baz"
 */
export const removeDotsFromArrayPath = (path: string) => path.replace(/\.\[/g, "[");

/**
 * Wraps elements of the path containing " ", "-" within quotes to ensure that they are valid JSON keys.
 *
 * @example sanitizePath("functionalGroups[0].trailer.GE-01") => `functionalGroups[0].trailer."GE-01"`
 * @example sanitizePath("functionalGroups[0].segment.GP 01") => `functionalGroups[0].segment."GP 01"`
 */
export const sanitizePath = (path: string) => {
  const pathWithoutArrayDots = removeDotsFromArrayPath(path);

  return splitPathBySymbolUnlessWithinQuotesOrBackticks(pathWithoutArrayDots, ".")
    .map((segment) => sanitizePathSegment(segment))
    .join(".");
};

export const sanitizePathSegment = (segment: string): string => {
  if (segment.startsWith("`") && segment.endsWith("`")) {
    // If it's already wrapped in backticks - skip escaping logic below
    return segment;
  }

  if (segment.startsWith('"') && segment.endsWith('"')) {
    // If it's wrapped in double-quotes - rewrap in backticks for consistency
    return `\`${segment.slice(1, -1)}\``;
  }

  if (segment.includes("[")) {
    // Splitting an array segment, e.g. transactions_01[0] to transactions_01 and [0]
    // so that the part before `[` can be sanitized properly
    const [firstPart, ...restOfParts] = splitPathBySymbolUnlessWithinQuotesOrBackticks(segment, "[");
    if (restOfParts.length > 0) {
      return `${sanitizePathSegment(firstPart)}[${restOfParts.join("[")}`;
    }
  }

  /**
   * The RegExp was split into multiple ones to avoid unnecessary complexity.
   */

  /**
   * [^ -> match single character NOT present in
   * a-z -> lower case letters
   * A-Z -> upper case letters
   * _ -> underscore
   * [ -> the '[' symbol
   * \] -> the ']'
   * -> \d+ 1 and more digits. The case where the path starts with a digit is handled in the next if statement
   * ] -> close the group
   */
  if (/[^a-zA-Z_[\]\d+]/.test(segment)) {
    return `\`${segment}\``;
  }

  /**
   * ^ -> starts with
   * \d+ match 1 or more digits
   */
  if (/^\d+/.test(segment)) {
    return `\`${segment}\``;
  }

  return segment;
};

/**
 * Splits a string by special symbols (e.g. dots) but ignore those symbols within double-quotes or backticks.
 * Inspired by https://stackoverflow.com/a/11457952
 */
export const splitPathBySymbolUnlessWithinQuotesOrBackticks = (path: string, splitBy: string): string[] => {
  const sanitizedSplitBy = sanitizeRegExpParameter(splitBy);

  /**
   * matching examples (assuming splitBy = "."):
   * - "foo"
   * - "foo.bar"
   * - "foo.bar"[0]
   * - foo"bar.baz"[0]
   * - foo"bar.baz`bee"[0]
   */
  const anythingWithSegmentsInDoubleQuotes = `[^"\\${sanitizedSplitBy}]*"[^"]*"[^"\\${sanitizedSplitBy}]*`;

  /**
   * matching examples (assuming splitBy = "."):
   * - `foo`
   * - `foo.bar`
   * - `foo.bar`[0]
   * - foo`bar.baz`[0]
   * - foo`bar.baz"bee`[0]
   */
  const anythingWithSegmentsInBackticks = `[^\`\\${sanitizedSplitBy}]*\`[^\`]*\`[^\`\\${sanitizedSplitBy}]*`;

  /**
   * matching examples (assuming splitBy = "."):
   * - foo
   * - foo1
   * - foo[0]
   */
  const noQuotesOrBackticks = `[^\`"\\${sanitizedSplitBy}]+`;

  const regexpOptions = [anythingWithSegmentsInDoubleQuotes, anythingWithSegmentsInBackticks, noQuotesOrBackticks];
  const regexp = new RegExp(`(${regexpOptions.join("|")})`, "g");

  return path.match(regexp) ?? [path];
};

export const sanitizeRegExpParameter = (regExpParameter: string) => {
  // Adapted from https://stackoverflow.com/a/17886301
  return regExpParameter.replace(/[-\\/\\^$*+?.()|[\]{}]/g, "\\$&");
};
